/**
 * Hook da janela navegável de 6dus (Task #170 — GET /api/calendario/6dus).
 *
 * Retorna o array de 6dus (últimos `prev` + próximo + próximos `next`) já
 * parseado pra Date + a data do próximo oficial (is_proximo). Cache
 * module-level TTL 24h (mesma estratégia do useProximo6du — a janela muda
 * no máximo 1×/dia). Em falha devolve array vazio: a navegação fica
 * desabilitada e o Card usa só o próximo oficial do useProximo6du.
 */
import { useEffect, useState } from "react";
import { getCalendario6dus } from "../api/client";
import type { FonteCalendario6du } from "../api/types";
import { ymdIso } from "../utils/dateBr";

const TTL_MS = 24 * 60 * 60 * 1000;
const PREV = 6;
const NEXT = 12;

export interface Calendario6dus {
  /** 6dus em ordem cronológica (Date local meia-noite). */
  calendario: Date[];
  /** Próximo 6du oficial (is_proximo) ou null se não houver futuro/dados. */
  proximoOficial: Date | null;
  /** Task #206: mapa ymdIso(dt_6du) → mes_referencia ("YYYY-MM") do
   * fornecimento que liquida naquele 6du. Usado pra filtrar a Posição
   * Aberta do Card por mês ao navegar prev/next (antes o KPI congelava
   * no próximo oficial — endpoint sempre recebia o default). Chaveado por
   * `ymdIso` pra casar com o lookup do consumidor independente de fuso. */
  mesRefByData: Record<string, string>;
  /** #62 (b'): ymdIso(dt_6du) → true quando o 6du é ESTIMADO (proxy além do
   * calendário oficial) — o card rotula a navegação como estimativa. */
  estimadoByData: Record<string, boolean>;
  /** 16/10: ymdIso(dt_6du) → fonte da data (oficial, estimado gravado ou
   * proxy) — escolhe o tooltip do badge de estimativa. */
  fonteByData: Record<string, FonteCalendario6du>;
}

interface CacheEntry {
  expiresAt: number;
  promise: Promise<Calendario6dus>;
}

let cache: CacheEntry | null = null;

export function invalidateCalendario6dusCache(): void {
  cache = null;
}

function parseLocal(ymd: string): Date {
  return new Date(ymd + "T00:00:00");
}

function fetchCalendario(): Promise<Calendario6dus> {
  return getCalendario6dus({ prev: PREV, next: NEXT })
    .then((items) => ({
      calendario: items.map((i) => parseLocal(i.data)),
      proximoOficial: (() => {
        const p = items.find((i) => i.is_proximo);
        return p ? parseLocal(p.data) : null;
      })(),
      mesRefByData: Object.fromEntries(
        items.map((i) => [ymdIso(parseLocal(i.data)), i.mes_referencia]),
      ),
      estimadoByData: Object.fromEntries(
        items.map((i) => [ymdIso(parseLocal(i.data)), i.estimado === true]),
      ),
      fonteByData: Object.fromEntries(
        items.map((i) => [
          ymdIso(parseLocal(i.data)),
          i.fonte ?? (i.estimado ? "proxy_6du" : "calendario_ccee"),
        ]),
      ),
    }))
    .catch(() => ({
      calendario: [],
      proximoOficial: null,
      mesRefByData: {},
      estimadoByData: {},
      fonteByData: {},
    }));
}

export interface UseCalendario6dusState {
  calendario: Date[];
  proximoOficial: Date | null;
  mesRefByData: Record<string, string>;
  estimadoByData: Record<string, boolean>;
  fonteByData: Record<string, FonteCalendario6du>;
  loading: boolean;
}

export function useCalendario6dus(): UseCalendario6dusState {
  const [state, setState] = useState<UseCalendario6dusState>({
    calendario: [],
    proximoOficial: null,
    mesRefByData: {},
    estimadoByData: {},
    fonteByData: {},
    loading: true,
  });

  useEffect(() => {
    let cancel = false;
    const now = Date.now();
    if (!cache || cache.expiresAt < now) {
      cache = { expiresAt: now + TTL_MS, promise: fetchCalendario() };
    }
    cache.promise.then((data) => {
      if (cancel) return;
      setState({ ...data, loading: false });
    });
    return () => {
      cancel = true;
    };
  }, []);

  return state;
}
